import { useRef, useState, useEffect, KeyboardEvent, ChangeEvent } from 'react';
import {
  Box,
  IconButton,
  InputBase,
  CircularProgress,
  Typography,
} from '@mui/material';
import { IconSend, IconPhoto, IconX } from '@tabler/icons';
import { t } from 'i18next';
import toast from 'react-hot-toast';

interface ChatComposerProps {
  onSend: (text: string) => Promise<void> | void;
  onSendImage: (file: File, caption?: string) => Promise<void>;
  isUploading?: boolean;
  uploadProgress?: number | null;
  disabled?: boolean;
}

const ChatComposer = ({
  onSend,
  onSendImage,
  isUploading,
  uploadProgress,
  disabled,
}: ChatComposerProps) => {
  const [value, setValue] = useState('');
  const [sending, setSending] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messageInputRef = useRef<HTMLInputElement>(null);

  // Revoke the object URL whenever it's replaced/cleared to avoid leaking memory.
  useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const isBusy = sending || isUploading || disabled;

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(t('Chat.invalidImageType'));
      
return;
    }

    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleRemoveAttachment = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setSelectedFile(null);
    setPreviewUrl(null);
  };

  const handleSend = async () => {
    const trimmed = value.trim();
    if (isBusy) return;
    if (!trimmed && !selectedFile) return;

    setSending(true);
    setValue('');
    // Refocus immediately so the user can start the next message without
    // waiting for the send to finish (the send button takes focus on click).
    requestAnimationFrame(() => {
      messageInputRef.current?.focus();
    });
    try {
      if (selectedFile) {
        await onSendImage(selectedFile, trimmed);
        handleRemoveAttachment();
      } else {
        await onSend(trimmed);
      }
    } catch {
      // Keep the draft only if the user has not already started typing
      // the next message while this send was in flight.
      setValue((current) => (current === '' ? trimmed : current));
    } finally {
      setSending(false);
      requestAnimationFrame(() => {
        messageInputRef.current?.focus();
      });
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ borderTop: '1px solid #EEF2F6' }}>
      {selectedFile && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '12px 24px 0',
          }}
        >
          <Box sx={{ position: 'relative', width: 52, height: 52, flexShrink: 0 }}>
            <Box
              component="img"
              src={previewUrl ?? undefined}
              alt={selectedFile.name}
              sx={{
                width: 52,
                height: 52,
                borderRadius: '8px',
                objectFit: 'cover',
                opacity: isUploading ? 0.5 : 1,
              }}
            />
            {isUploading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress
                  size={24}
                  variant={uploadProgress != null ? 'determinate' : 'indeterminate'}
                  value={uploadProgress ?? undefined}
                />
              </Box>
            )}
            {!isUploading && (
              <IconButton
                onClick={handleRemoveAttachment}
                size="small"
                aria-label={t('Chat.removeAttachment')}
                sx={{
                  position: 'absolute',
                  top: -8,
                  right: -8,
                  width: 20,
                  height: 20,
                  backgroundColor: '#22242E',
                  color: '#fff',
                  '&:hover': { backgroundColor: '#000' },
                }}
              >
                <IconX size={12} />
              </IconButton>
            )}
          </Box>
          <Typography sx={{ fontSize: '12px', color: '#8B95A1' }}>
            {isUploading ? t('Chat.uploadingImage') : selectedFile.name}
          </Typography>
        </Box>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '18px 24px',
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFileChange}
        />
        <IconButton
          onClick={handleAttachClick}
          disabled={isBusy}
          aria-label={t('Chat.attachImage')}
          sx={{ color: '#8B95A1', flexShrink: 0 }}
        >
          <IconPhoto size={20} />
        </IconButton>
        <InputBase
          inputRef={messageInputRef}
          value={value}
          onChange={(e: ChangeEvent<HTMLInputElement>) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            selectedFile ? t('Chat.addCaption') : t('Chat.typeMessage')
          }
          disabled={disabled || isUploading}
          fullWidth
          inputProps={{ 'aria-label': t('Chat.typeMessage') }}
          sx={{
            border: '1px solid #DFE5EC',
            borderRadius: '22px',
            padding: '12px 18px',
            fontSize: '13.5px',
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={(!value.trim() && !selectedFile) || isBusy}
          aria-label={t('Chat.send')}
          sx={{
            width: 42,
            height: 42,
            backgroundColor: '#84BD00',
            color: '#fff',
            flexShrink: 0,
            '&:hover': { backgroundColor: '#6FA323' },
            '&.Mui-disabled': { backgroundColor: '#DDEFC9', color: '#fff' },
          }}
        >
          {sending ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <IconSend size={19} />}
        </IconButton>
      </Box>
    </Box>
  );
};

export default ChatComposer;
